import { useQuery, useMutation } from '@tanstack/react-query';
import axiosInstance from './axios';

export type AccountingEntry = {
  referenceId: string;
  credit: number | null;
  debit: number | null;
  method: string | null;
  source?: string | null;
  date?: string | null;
};

export type AccountingResponse = {
  entries: AccountingEntry[];
  totalCredit: number;
  totalDebit: number;
  bankClosingAmount: number;
  physicalCashCount: number;
  receivable?: number;
  payable?: number;
};

export type GetAccountingRequest = {
  branchId?: string | number;
  counterId?: string | number;
  userId?: string | number;
};

export type GetAccountingEntriesRequest = {
  branchId?: string | number;
  counterId?: string | number;
  staffId?: string | number;
  date: string;
  pageNo: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
};

export interface CounterEntry {
  referenceId: string;
  credit: number | null;
  debit: number | null;
  method: string | null;
  source?: string | null;
  date?: string | null;
}

export interface CounterEntriesResponse {
  entries: CounterEntry[];
  totalCredit: number;
  totalDebit: number;
  totalPages: number;
  totalElements: number;
  expectedCash: number;
  expectedBank: number;
  openingBalance: number;
  isSessionOpen: boolean;
  counterSessionId: number | null;
  receivable: number;
  payable: number;
}

export const useGetAllAccountingEntries = (
  params: GetAccountingEntriesRequest
) => {
  return useQuery({
    queryKey: ['useGetAllAccountingEntries', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/accounting/accounting', {
        params,
      });
      return data as CounterEntriesResponse;
    },
    enabled: !!(params.branchId || params.counterId),
  });
};

export const useDownloadCashReportExcel = () => {
  return useMutation({
    mutationFn: async (params: { date: string; branchId: number }) => {
      const response = await axiosInstance.get(
        '/daily-reports/cash-report/export/excel',
        { params, responseType: 'blob' }
      );
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-cash-${variables.date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};
